/**
 * The money display mode, for every component that prints an amount.
 *
 * A context rather than a prop threaded through four components, because this is a
 * viewer preference and not a property of any one table: the seat boxes, the pot, the
 * action bar and the hand history must all switch together or the screen contradicts
 * itself mid-hand. Anything that renders an amount reads `useMoney()`.
 *
 * The table's own big blind and format come from the game, not from the preference —
 * so `formatMoney` is always answering "how is THIS table's amount written", never
 * guessing at a unit.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  formatMoney, speakMoney, loadMoneyMode, saveMoneyMode, onMoneyModeChange, bucksMarked,
  MONEY_MODES, type MoneyMode, type TableFormat,
} from "./money";
import { BucksIcon } from "./mark";

interface MoneyState {
  mode: MoneyMode;
  setMode: (m: MoneyMode) => void;
  /** This amount, written for this table. */
  fmt: (v: number) => string;
  /** The same, spelled out for a screen reader. */
  say: (v: number) => string;
  /**
   * The amount as something to render, coin included.
   *
   * `fmt` stays a string because most callers put an amount inside a longer sentence
   * and because `lib/money.ts` has to stay importable by tests with no DOM. This is the
   * other half: on a Bucks table it returns the brass coin followed by the grouped
   * number, and everywhere else it returns exactly what `fmt` returns. A component that
   * wants the mark asks for `amount`; one that wants a string still asks for `fmt`.
   */
  amount: (v: number) => ReactNode;
}

/**
 * The coin, then the number.
 *
 * Shared by the provider and the fallback so there is one definition of what a Bucks
 * amount looks like. `aria-hidden` on the coin: `say()` already spells the unit out,
 * and a screen reader announcing "image, twelve thousand four hundred Bucks" would say
 * it twice.
 */
function marked(text: string, coin: boolean): ReactNode {
  if (!coin) return text;
  return <span className="bucks-amount"><BucksIcon className="bucks-mark" />{text}</span>;
}

const Ctx = createContext<MoneyState | null>(null);

export function MoneyProvider(
  { bigBlind, format, pin, children }:
  /*
   * `pin` (Phase 8): a table whose unit is not the player's to choose. Blackjack has no
   * big blind, so the poker toggle's "bb" printed a green chip as "1bb" on the felt and
   * in the bet stepper. Pinned, the provider ignores the saved choice; unpinned —
   * every poker caller — it is exactly what it was.
   */
  { bigBlind: number; format: TableFormat; pin?: MoneyMode; children: ReactNode },
) {
  const [chosen, setModeState] = useState<MoneyMode>(loadMoneyMode);
  const mode = pin ?? chosen;

  const setMode = useCallback((m: MoneyMode) => {
    setModeState(m);
    saveMoneyMode(m);
  }, []);

  /*
   * Phase 2b: the Settings sheet can change this from outside the provider.
   *
   * `saveMoneyMode` notifies; this picks it up. Without it a player who switched to big
   * blinds in the sheet went back to a table still printing chips, and the two controls
   * for one setting disagreed until the page was reloaded.
   */
  useEffect(() => onMoneyModeChange(() => setModeState(loadMoneyMode())), []);

  const value = useMemo<MoneyState>(() => ({
    mode,
    setMode,
    fmt: (v: number) => formatMoney(v, { mode, bigBlind, format }),
    say: (v: number) => speakMoney(v, { mode, bigBlind, format }),
    amount: (v: number) => marked(
      formatMoney(v, { mode, bigBlind, format }),
      bucksMarked({ mode, bigBlind, format }),
    ),
  }), [mode, setMode, bigBlind, format]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Amounts, in the viewer's chosen unit.
 *
 * Falls back to plain chips outside a provider rather than throwing. A component that
 * prints a number is not the right place to take the whole screen down, and the
 * fallback is exactly what the table did before the toggle existed.
 */
/**
 * The control.
 *
 * A segmented pair rather than a dropdown: there are exactly two options, both are
 * one word, and a select for two values costs a tap and hides the alternative. It
 * carries a radiogroup role so a screen reader announces it as the choice it is —
 * two buttons would be announced as two unrelated actions.
 */
export function MoneyToggle({ className = "" }: { className?: string }) {
  const { mode, setMode } = useMoney();
  return (
    <div className={`money-toggle ${className}`.trim()}
         role="radiogroup" aria-label="Show amounts as">
      {MONEY_MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          role="radio"
          aria-checked={mode === m.id}
          className={mode === m.id ? "is-on" : ""}
          title={m.hint}
          onClick={() => setMode(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

export function useMoney(): MoneyState {
  const v = useContext(Ctx);
  if (v) return v;
  const fallback: MoneyState = {
    mode: "native",
    setMode: () => {},
    fmt: (n: number) => formatMoney(n, { mode: "native", bigBlind: 1, format: "tournament" }),
    say: (n: number) => speakMoney(n, { mode: "native", bigBlind: 1, format: "tournament" }),
    amount: (n: number) => formatMoney(n, { mode: "native", bigBlind: 1, format: "tournament" }),
  };
  return fallback;
}
